import { createActions, createNested, Nested, OnSignal } from 'dreamstate';

import { Callable, Id, Optional } from '@vimeo/core/types/generic';
import { Logger } from '@vimeo/logging';

import { firebaseConfig } from '../../../../configs/FirebaseConfig';
import { interactionToolsConfig } from '../../../../configs/InteractionToolsConfig';
import { ELiveErrorCode } from '../../../../types/error';
import {
  IFirebaseDependencies,
  TFirebaseDatabase,
  TFirebaseReference,
} from '../../../../types/firebase';
import {
  EChatType,
  EInteractionModule,
  TChatBanList,
  TChatHistory,
  TChatParticipants,
  TInteractionHistoryConnection,
} from '../../../../types/interaction_tools';
import { IInteractionStoreInitialState } from '../../../../types/store';
import { LiveError } from '../../../../utils/error';
import { getDatabaseRef, getRealtimeDatabase } from '../../../../utils/firebase/cruds';
import { getAbsoluteNow } from '../../../../utils/general/time';
import { checkModuleIsActive } from '../../../../utils/interaction_tools/module';
import { IInMemoryBufferConfig, InMemoryBuffer } from '../../../../utils/store/memory_buffer';
import { UnsubscribingContextManager } from '../../../../utils/store/unsubscribing_manager';
import {
  ELiveErrorCategory,
  trackLiveAction,
  withLiveErrorTracking,
} from '../../../../utils/tracking';
import {
  ELiveRealtimeSignal,
  ELiveSignal,
  TFirebaseInteractionAppReadySignalEvent,
} from '../../../signals';
import {
  blockChatUser,
  deleteChatMessage,
  dispose,
  sendChatMessage,
  setChatEnabled,
  unblockChatUser,
} from './ChatManager.actions';
import {
  hydrateBanList,
  hydrateChatHistory,
  subscribeToBanList,
  subscribeToChatMessages,
  subscribeToChatStatus,
} from './ChatManager.subscription';

interface IChatDescriptor {
  participants: TChatParticipants;
  messages: TChatHistory;
  banList: TChatBanList;
}

export interface IChatBufferValue {
  participants: TChatParticipants;
  messages: TChatHistory;
}

export interface IChatContext {
  isEnabled: Optional<boolean>;
  isBackstageEnabled: Optional<boolean>;
  isHydrated: boolean;
  enabledAt: Optional<number>;
  hydratedAt: Optional<number>;
  config: {
    roomId: Id;
    isCreator: boolean;
  };
  chatActions: {
    enableChat(): void;
    disableChat(): void;
    sendMessage(chatType: EChatType, message: string): Promise<void>;
    deleteMessage(chatType: EChatType, messageId: string): void;
    blockChatUser(chatType: EChatType, userId: string): Promise<void>;
    unblockChatUser(chatType: EChatType, userId: string): Promise<void>;
  };
  [EChatType.PUBLIC]: Nested<IChatDescriptor>;
  [EChatType.BACKSTAGE]: Nested<IChatDescriptor>;
  chatSessionsHistory: Array<TInteractionHistoryConnection>;
}

/**
 * Chat messages manager, subscribe/unsubscribe from messages, send anything to chat from here.
 */
export class ChatManager extends UnsubscribingContextManager<IChatContext> {
  public static createNewChatDescriptor(): Nested<IChatDescriptor> {
    return createNested({
      participants: {},
      messages: {},
      banList: {},
    });
  }

  private static createBufferConfig(
    manager: ChatManager,
    name: string,
    chatType: EChatType,
  ): IInMemoryBufferConfig<IChatBufferValue> {
    return {
      name,
      initialValue: { messages: {}, participants: {} },
      throttleDelay: interactionToolsConfig.CHAT.MESSAGE_UPDATE_THROTTLE,
      onUpdate: (value: IChatBufferValue) => {
        manager.setContext({
          [chatType]: manager.context[chatType].asMerged(value),
        });
      },
    };
  }

  public override context: IChatContext = {
    chatActions: createActions({
      enableChat: () => this.setChatEnabled(true),
      disableChat: () => this.setChatEnabled(false),
      sendMessage: (chatType: EChatType, message: string) => this.sendMessage(chatType, message),
      deleteMessage: (chatType: EChatType, messageId: string) =>
        this.deleteMessage(chatType, messageId),
      blockChatUser: (chatType: EChatType, userId: string) => this.blockChatUser(chatType, userId),
      unblockChatUser: (chatType: EChatType, userId: string) =>
        this.unblockChatUser(chatType, userId),
    }),
    [EChatType.PUBLIC]: ChatManager.createNewChatDescriptor(),
    [EChatType.BACKSTAGE]: ChatManager.createNewChatDescriptor(),
    isEnabled: null,
    enabledAt: null,
    isBackstageEnabled: null,
    isHydrated: false,
    hydratedAt: null,
    // todo: Unpack, get room type and hash
    config: {
      roomId: -1,
      isCreator: false,
    },
    chatSessionsHistory: [],
  };

  public readonly log: Logger = new Logger('🥨CHT');
  /**
   * Whether allowed to use backstage in current context.
   */
  public readonly isBackstageAllowed: boolean = false;
  /**
   * Whether the chat module is active in the current context.
   */
  public readonly isModuleActive: boolean = false;

  public firebase!: IFirebaseDependencies;
  public publicChatBuffer: Optional<InMemoryBuffer<IChatBufferValue>> = null;
  public backstageChatBuffer: Optional<InMemoryBuffer<IChatBufferValue>> = null;
  public publicChatRef: Optional<TFirebaseReference> = null;
  public guestsChatRef: Optional<TFirebaseReference> = null;
  public metaChatEnabledRef: Optional<TFirebaseReference> = null;
  public publicChatBanListRef: Optional<TFirebaseReference> = null;

  public constructor(initialState?: IInteractionStoreInitialState) {
    super();
    /*
     * Allow backstage usage if:
     *  - View supports and requires it
     *  - Management part is not disabled for the widget
     */
    this.isBackstageAllowed = Boolean(
      initialState?.interaction?.feature?.isBackstageAllowed &&
        !initialState?.interaction?.feature?.isManagementDisabled,
    );

    this.isModuleActive = checkModuleIsActive(
      initialState?.interaction?.feature?.module,
      EInteractionModule.CHAT,
    );
  }

  /**
   * Cleanup of subscription data.
   *  - Unsubscribe from collections
   *  - Prevent future usage of refs mutations after manager unload
   */
  public override onProvisionEnded(): void {
    return dispose(this);
  }

  /**
   * Assert current manager is initialized and can continue working with current scope.
   */
  public assertIsInitialized(): asserts this is {
    publicChatRef: TFirebaseReference;
    guestsChatRef: TFirebaseReference;
    metaChatEnabledRef: TFirebaseReference;
    publicChatBanListRef: TFirebaseReference;
  } {
    if (
      !this.publicChatRef ||
      !this.guestsChatRef ||
      !this.metaChatEnabledRef ||
      !this.publicChatBanListRef
    ) {
      throw new LiveError('Chat manager is not initialized.', {
        code: ELiveErrorCode.INITIALIZATION,
      });
    }
  }

  /**
   * Get chat ref by chat type.
   */
  public getChatRef(chatType: EChatType): TFirebaseReference {
    this.assertIsInitialized();

    return chatType === EChatType.PUBLIC ? this.publicChatRef : this.guestsChatRef;
  }

  /**
   * Get ban list ref by chat type.
   */
  public getChatBanListRef(chatType: EChatType): Optional<TFirebaseReference> {
    this.assertIsInitialized();

    return chatType === EChatType.PUBLIC ? this.publicChatBanListRef : null;
  }

  /**
   * Enable chat widget.
   * Does not affect guest chat.
   */
  public async setChatEnabled(isEnabled: boolean): Promise<void> {
    return withLiveErrorTracking(() => setChatEnabled(this, isEnabled), {
      method: 'setChatEnabled',
      category: ELiveErrorCategory.INTERACTION,
      data: {
        metaChatEnabledRef: this.metaChatEnabledRef?.toString(),
      },
    });
  }

  /**
   * Send message to the chat.
   */
  public async sendMessage(chatType: EChatType, rawMessage: string): Promise<void> {
    return withLiveErrorTracking(() => sendChatMessage(this, chatType, rawMessage), {
      method: 'sendChatMessage',
      category: ELiveErrorCategory.INTERACTION,
      data: {
        publicChatRef: this.publicChatRef?.toString(),
        guestsChatRef: this.guestsChatRef?.toString(),
      },
    });
  }

  /**
   * Delete message by ID.
   */
  public async deleteMessage(chatType: EChatType, messageId: string): Promise<void> {
    return withLiveErrorTracking(() => deleteChatMessage(this, chatType, messageId), {
      method: 'deleteChatMessage',
      category: ELiveErrorCategory.INTERACTION,
    });
  }

  /**
   * Shadow-ban chat user and hide their messages from others.
   */
  public async blockChatUser(chatType: EChatType, userId: string): Promise<void> {
    return withLiveErrorTracking(() => blockChatUser(this, chatType, userId), {
      method: 'blockChatUser',
      category: ELiveErrorCategory.INTERACTION,
    });
  }

  /**
   * Unban chat user and show their messages to others.
   */
  public async unblockChatUser(chatType: EChatType, userId: string): Promise<void> {
    return withLiveErrorTracking(() => unblockChatUser(this, chatType, userId), {
      method: 'unblockChatUser',
      category: ELiveErrorCategory.INTERACTION,
    });
  }

  @OnSignal(ELiveRealtimeSignal.FIREBASE_INTERACTION_APP_READY)
  private async onChatApplicationReady({
    data: { firebase, app, roomId, user, metadata },
  }: TFirebaseInteractionAppReadySignalEvent): Promise<void> {
    this.firebase = firebase;

    if (this.isModuleActive) {
      this.log.info('🚀Initializing chat connection:', roomId, user);
      trackLiveAction('fb_init_chat_manager');

      const database: TFirebaseDatabase = getRealtimeDatabase(firebase, app);

      this.publicChatRef = getDatabaseRef(
        firebase,
        database,
        firebaseConfig.REFS.INTERACTION.CHAT.PUBLIC(roomId),
      );
      this.guestsChatRef = getDatabaseRef(
        firebase,
        database,
        firebaseConfig.REFS.INTERACTION.CHAT.GUEST(roomId),
      );
      this.metaChatEnabledRef = getDatabaseRef(
        firebase,
        database,
        firebaseConfig.REFS.INTERACTION.CHAT.META_ENABLED(roomId),
      );
      this.publicChatBanListRef = getDatabaseRef(
        firebase,
        database,
        firebaseConfig.REFS.INTERACTION.CHAT.PUBLIC_BANS(roomId),
      );

      /**
       * todo: Extend with capability with new API endpoints to ensure backstage is allowed.
       */
      const isBackstageEnabled: boolean = this.isBackstageAllowed;
      this.log.info('🚀Backstage access status:', isBackstageEnabled);

      this.setContext({
        isBackstageEnabled,
        chatSessionsHistory: metadata?.connections?.chatHistory || [],
        config: { roomId, isCreator: Boolean(user.isCreator) },
      });

      this.publicChatBuffer = new InMemoryBuffer<IChatBufferValue>(
        ChatManager.createBufferConfig(this, 'publicChatBuffer', EChatType.PUBLIC),
      );

      const hydrations: Array<Promise<void>> = [
        hydrateChatHistory(this, this.publicChatRef, this.publicChatBuffer),
        hydrateBanList(this, EChatType.PUBLIC),
      ];

      const subscriptions: Array<Callable> = [
        subscribeToChatStatus(this, this.metaChatEnabledRef),
        subscribeToChatMessages(this, this.publicChatRef, this.publicChatBuffer),
        subscribeToBanList(this, EChatType.PUBLIC),
      ];

      if (isBackstageEnabled) {
        this.backstageChatBuffer = new InMemoryBuffer<IChatBufferValue>(
          ChatManager.createBufferConfig(this, 'backstageChatBuffer', EChatType.BACKSTAGE),
        );

        hydrations.push(
          hydrateChatHistory(
            this,
            this.guestsChatRef,
            this.backstageChatBuffer as InMemoryBuffer<IChatBufferValue>,
          ),
        );

        subscriptions.push(
          subscribeToChatMessages(
            this,
            this.guestsChatRef,
            this.backstageChatBuffer as InMemoryBuffer<IChatBufferValue>,
          ),
        );
      }

      /**
       * If promise is not blocking,
       * we still should account it to prevent untracked async code execution.
       */
      await Promise.all(hydrations).finally(() => {
        this.setContext({
          isHydrated: true,
          hydratedAt: getAbsoluteNow(),
        });
      });

      this.addUnSubscribers(subscriptions);
    } else {
      this.log.info('🚀Skipping chat connection');
    }
  }

  /**
   * On logout clean up previous information.
   */
  @OnSignal(ELiveSignal.INTERACTION_SESSION_LOGOUT)
  private onInteractionSessionLogout(): void {
    return dispose(this);
  }
}
